// frontend/src/App.tsx
// Componente principal de la aplicación SGCC — Stitch Institutional Design

import { useState, useEffect, useRef, lazy, Suspense } from 'react';

// Declaración global de la versión de compilación de la app (inyectada por Vite)
declare const __APP_VERSION__: string;

// Misma base que usa el cliente API (lib/api.ts no la exporta).
const API_BASE_URL: string = import.meta.env.VITE_API_URL || '/api';
const COMPANIA_STORAGE_KEY = 'sgcc_compania_v4';
const UPDATE_RELOAD_KEY = 'sgcc_update_reloaded_for';
import { TopAppBar, COMPANIAS } from './components/TopAppBar';
import { BannerApp } from './components/BannerApp';
import { BottomNavBar, type NavTab } from './components/BottomNavBar';
import { CadetesList } from './components/cadetes/CadetesList';
import { ReportesTab } from './components/ReportesTab';
import { InitScreen } from './components/InitScreen';
import { LoginScreen } from './components/auth/LoginScreen';
import { CambiarPasswordScreen } from './components/auth/CambiarPasswordScreen';
import { useRuta } from './hooks/useRuta';
import { ErrorCargaAdmin } from './components/admin/ErrorCargaAdmin';
import { storage } from './services/storage';
import { api, EVENTO_CAMBIO_PASSWORD, type UsuarioSesion } from './lib/api';
import { useOfflineSync } from './hooks/useOfflineSync';
import { useIOSViewportStabilizer } from './hooks/useIOSViewportStabilizer';
import { Calendar, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { getArgentinaDateString, toLocalDateString } from './lib/utils';
import { resolverCompaniaUsuario, leerCompaniaSesionGuardada } from './lib/companiaSesion';
import { getMarcasEnVuelo, esperarMarcasEnVuelo, solicitarFlushMarcas } from './lib/marcasEnVuelo';
import { toast } from './lib/toast';
import { AnimatePresence, m } from 'motion/react';
import { DUR, EASE_IOS } from './lib/motion';

interface UserSession {
  token: string;
  user: UsuarioSesion;
}

// Panel de administración: sólo para admin, en un chunk aparte (no se descarga
// hasta entrar a /admin)
const RUTA_ADMIN = '/admin';
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));

// Compañía forzada por la sesión para usuarios no-admin (null = libre selección
// o compañía no resoluble). Siempre es una entrada válida de COMPANIAS.
function getCompaniaForzada(session: UserSession | null): string | null {
  if (!session || session.user.scope === 'admin') return null;
  return resolverCompaniaUsuario(session.user);
}

// Resultado de un intento de actualización forzada
type UpdateResult = 'ok' | 'guard' | 'pending' | 'offline';

// ──────────────────────────────────────────
// Date Selector (for Dashboard tab header)
// ──────────────────────────────────────────
function DateSelector({
  fecha,
  onChange,
}: {
  fecha: string;
  onChange: (f: string) => void;
}) {
  const cambiarDia = (dias: number) => {
    const d = new Date(fecha + 'T00:00:00');
    d.setDate(d.getDate() + dias);
    onChange(toLocalDateString(d));
  };

  const irAHoy = () => onChange(getArgentinaDateString());
  const esHoy = fecha === getArgentinaDateString();

  // Dirección del último cambio (+1 adelante, -1 atrás) para deslizar el texto
  const fechaPreviaRef = useRef(fecha);
  const direccionRef = useRef(1);
  if (fechaPreviaRef.current !== fecha) {
    direccionRef.current = fecha > fechaPreviaRef.current ? 1 : -1;
    fechaPreviaRef.current = fecha;
  }
  const dir = direccionRef.current;

  const formatFecha = (f: string) => {
    const d = new Date(f + 'T00:00:00');
    return d.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    });
  };


  return (
    <div
      className="flex items-center justify-between px-4 py-2 border-b"
      style={{
        backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
        borderColor: 'var(--color-sgcc-outline-variant)',
      }}
    >
      <button
        className="w-9 h-9 rounded-lg flex items-center justify-center tap-active border"
        style={{
          borderColor: 'var(--color-sgcc-outline-variant)',
          color: 'var(--color-sgcc-on-surface-variant)',
        }}
        onClick={() => cambiarDia(-1)}
        aria-label="Día anterior"
      >
        <ChevronLeft size={20} strokeWidth={2} />
      </button>

      <button
        className="flex items-center gap-2 px-4 py-1.5 rounded-lg tap-active overflow-hidden"
        style={{
          backgroundColor: esHoy ? 'var(--color-sgcc-secondary-container)' : 'var(--color-sgcc-surface-container)',
          color: esHoy ? 'var(--color-sgcc-on-secondary-container)' : 'var(--color-sgcc-on-surface)',
        }}
        onClick={irAHoy}
        aria-label="Ir a hoy"
      >
        <Calendar size={16} strokeWidth={2} />
        <AnimatePresence mode="popLayout" initial={false} custom={dir}>
          <m.span
            key={fecha}
            custom={dir}
            variants={{
              entra: (d: number) => ({ x: d * 18, opacity: 0 }),
              centro: { x: 0, opacity: 1 },
              sale: (d: number) => ({ x: d * -18, opacity: 0 }),
            }}
            initial="entra"
            animate="centro"
            exit="sale"
            transition={{ duration: DUR.base, ease: EASE_IOS }}
            className="text-sm font-semibold capitalize whitespace-nowrap"
          >
            {formatFecha(fecha)}
          </m.span>
        </AnimatePresence>
        {!esHoy && (
          <span
            className="text-[10px] font-medium px-1.5 py-0.5 rounded"
            style={{ backgroundColor: 'var(--color-sgcc-outline-variant)', color: 'var(--color-sgcc-outline)' }}
          >
            IR A HOY
          </span>
        )}
      </button>

      <button
        className="w-9 h-9 rounded-lg flex items-center justify-center tap-active border"
        style={{
          borderColor: esHoy ? 'var(--color-sgcc-outline-variant)' : 'var(--color-sgcc-outline-variant)',
          color: esHoy ? 'var(--color-sgcc-outline-variant)' : 'var(--color-sgcc-on-surface-variant)',
          opacity: esHoy ? 0.3 : 1,
        }}
        onClick={() => !esHoy && cambiarDia(1)}
        disabled={esHoy}
        aria-label="Día siguiente"
      >
        <ChevronRight size={20} strokeWidth={2} />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────
// Tab section title
// ──────────────────────────────────────────
function TabHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div
      className="px-4 py-3 border-b"
      style={{
        borderColor: 'var(--color-sgcc-outline-variant)',
        backgroundColor: 'var(--color-sgcc-surface-container)',
      }}
    >
      <p className="font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--color-sgcc-primary)' }}>
        {title}
      </p>
      {subtitle && (
        <p className="text-xs mt-0.5" style={{ color: 'var(--color-sgcc-outline)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────
// Full-screen loading
// ──────────────────────────────────────────
function LoadingScreen({ message }: { message: string }) {
  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center gap-4"
      style={{ backgroundColor: 'var(--color-sgcc-surface)' }}
    >
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center border-2"
        style={{
          backgroundColor: 'var(--color-sgcc-primary-container)',
          borderColor: 'var(--color-sgcc-on-primary-container)',
        }}
      >
        <RefreshCw size={32} strokeWidth={1.6} className="animate-spin-slow" style={{ color: 'var(--color-sgcc-on-primary-container)' }} />
      </div>
      <p className="text-sm" style={{ color: 'var(--color-sgcc-outline)' }}>{message}</p>
    </div>
  );
}

// ──────────────────────────────────────────
// App
// ──────────────────────────────────────────
function App() {
  const [fecha, setFecha] = useState(() => getArgentinaDateString());
  const [inicializado, setInicializado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<UserSession | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [activeTab, setActiveTab] = useState<NavTab>('dashboard');

  // Cambio de día con la app abierta (p. ej. una tablet que quedó encendida de
  // noche): si se estaba en la jornada de hoy, pasar a la nueva; si el usuario
  // eligió otra fecha a propósito, se respeta.
  const hoyRef = useRef(getArgentinaDateString());
  useEffect(() => {
    const verificarCambioDeDia = () => {
      const hoy = getArgentinaDateString();
      if (hoy === hoyRef.current) return;
      const ayer = hoyRef.current;
      hoyRef.current = hoy;
      setFecha((actual) => (actual === ayer ? hoy : actual));
    };
    const intervalo = window.setInterval(verificarCambioDeDia, 30_000);
    const alVolver = () => { if (document.visibilityState === 'visible') verificarCambioDeDia(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', verificarCambioDeDia);
    return () => {
      window.clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', verificarCambioDeDia);
    };
  }, []);

  const [companiaSeleccionada, setCompaniaSeleccionada] = useState<string>(() => {
    // Sesión restaurada de un no-admin: su compañía manda sobre lo guardado
    // (auth_user corrupto o sin compañía: se resuelve al verificar sesión)
    const guardada = leerCompaniaSesionGuardada();
    if (guardada && !guardada.esAdmin && guardada.compania) return guardada.compania;
    const saved = localStorage.getItem(COMPANIA_STORAGE_KEY);
    return saved && COMPANIAS.includes(saved) ? saved : 'Décima Compañía';
  });

  const companiaForzada = getCompaniaForzada(session);
  const isAdmin = session?.user.scope === 'admin';
  const { ruta, navegar } = useRuta();
  // Volver del panel: si se entró desde la app, retroceder (no apilar otra "/"
  // en el historial: el gesto atrás de Android reabriría el panel)
  const volverDeAdmin = () => {
    if ((window.history.state as { desdeApp?: boolean } | null)?.desdeApp) window.history.back();
    else navegar('/', { replace: true });
  };
  const debeCambiarPassword = session?.user.debe_cambiar_password === true;

  // /admin para quien no es admin: volver a "/" sin aviso (como si no existiera).
  // Se espera a que la sesión esté verificada para no echar a un admin.
  useEffect(() => {
    if (checkingSession) return;
    if (ruta === RUTA_ADMIN && (!session || !isAdmin || debeCambiarPassword)) navegar('/', { replace: true });
  }, [ruta, session, isAdmin, debeCambiarPassword, checkingSession, navegar]);

  // El servidor exige cambio de contraseña (restablecida mientras había sesión)
  useEffect(() => {
    const alExigir = () => {
      setSession((s) => {
        if (!s || s.user.debe_cambiar_password) return s;
        const user = { ...s.user, debe_cambiar_password: true };
        localStorage.setItem('auth_user', JSON.stringify(user));
        return { ...s, user };
      });
    };
    window.addEventListener(EVENTO_CAMBIO_PASSWORD, alExigir);
    return () => window.removeEventListener(EVENTO_CAMBIO_PASSWORD, alExigir);
  }, []);
  // No-admin cuya compañía no se pudo determinar: no mostrar una lista vacía
  const companiaNoResuelta = !!session && !isAdmin && !companiaForzada;

  // Usuarios no-admin: la compañía viene de la sesión (se ignora localStorage)
  useEffect(() => {
    if (companiaForzada && companiaSeleccionada !== companiaForzada) {
      setCompaniaSeleccionada(companiaForzada);
    }
  }, [companiaForzada, companiaSeleccionada]);

  // Persistir selección de compañía (solo admins eligen compañía)
  useEffect(() => {
    if (companiaSeleccionada && isAdmin) {
      localStorage.setItem(COMPANIA_STORAGE_KEY, companiaSeleccionada);
    }
  }, [companiaSeleccionada, isAdmin]);

  const handleCompaniaChange = (cia: string) => {
    if (!isAdmin && companiaForzada) return;
    setCompaniaSeleccionada(cia);
  };

  // Estados para el sistema de auto-actualización y control de caché (iOS / PWA)
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'up-to-date' | 'new-version' | 'updating' | 'error'>('idle');
  const [showUpdateOverlay, setShowUpdateOverlay] = useState(false);

  // Registrar cambios de estado de actualización para diagnóstico en consola (y satisfacer TS compiler)
  useEffect(() => {
    console.log('[PWA] Estado de actualización del cliente:', updateStatus);
  }, [updateStatus]);

  const { pendingCount, isSyncing } = useOfflineSync();

  // Refs para leer el estado actual de sync desde callbacks registrados una sola vez
  const isSyncingRef = useRef(isSyncing);
  isSyncingRef.current = isSyncing;
  const updatingRef = useRef(false);
  const checkingRef = useRef(false);
  const loggingOutRef = useRef(false);

  // ¿Es seguro recargar? Nunca durante una sync, con datos pendientes de subir ni
  // con marcas en vuelo (cuenta regresiva / geolocalización, aún no en IndexedDB).
  const puedeRecargarSinPerderDatos = async (): Promise<boolean> => {
    if (isSyncingRef.current) return false;
    if (getMarcasEnVuelo() > 0) return false;
    try {
      const pendientes = await storage.getPendingTotalCount();
      return pendientes === 0;
    } catch (err) {
      console.warn('[PWA] No se pudo verificar pendientes, se difiere la actualización:', err);
      return false;
    }
  };

  // Lógica de actualización forzada (bypasea el cacheo agresivo de iOS PWA).
  // Devuelve 'ok' si se recargó; si no, el motivo por el que se difirió/abortó.
  // `manual`: el usuario lo pidió explícitamente, se omite el guard anti-bucle.
  const forceAppUpdate = async (serverVersion: string, manual = false): Promise<UpdateResult> => {
    if (updatingRef.current) return 'guard';
    updatingRef.current = true;

    try {
      // Evitar bucles de recarga: no recargar dos veces por la misma versión
      let yaRecargado: string | null = null;
      try { yaRecargado = sessionStorage.getItem(UPDATE_RELOAD_KEY); } catch { /* noop */ }
      if (!manual && yaRecargado === serverVersion) {
        console.warn(`[PWA] Ya se recargó para la versión ${serverVersion}; se omite para evitar bucle.`);
        return 'guard';
      }

      if (!(await puedeRecargarSinPerderDatos())) {
        console.log('[PWA] Hay sincronización en curso, marcas en curso o registros pendientes. Actualización diferida.');
        return 'pending';
      }

      // Verificar que el nuevo index.html sea realmente accesible ANTES de tocar SW/cachés
      if (!navigator.onLine) return 'offline';
      try {
        const res = await fetch(`/index.html?v=${encodeURIComponent(serverVersion)}&t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        if (!html.includes('<div id="root"')) throw new Error('index.html inválido');
      } catch (err) {
        console.warn('[PWA] Nueva versión no accesible, se aborta la actualización:', err);
        return 'offline';
      }

      setUpdateStatus('updating');
      setShowUpdateOverlay(true);

      // Esperar 1.5 segundos para mostrar la pantalla de actualización premium
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Re-chequear: pudo haberse marcado algo durante la espera
      if (!(await puedeRecargarSinPerderDatos())) {
        console.log('[PWA] Aparecieron registros pendientes durante la espera. Actualización diferida.');
        setShowUpdateOverlay(false);
        setUpdateStatus('idle');
        return 'pending';
      }

      // 1. Desregistrar todos los service workers activos
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
            console.log('[PWA] Service Worker desregistrado.');
          }
        } catch (err) {
          console.error('[PWA] Error desregistrando SW:', err);
        }
      }

      // 2. Limpiar todos los caches locales de Cache Storage
      if ('caches' in window) {
        try {
          const keys = await caches.keys();
          for (const key of keys) {
            await caches.delete(key);
            console.log(`[PWA] Caché eliminada: ${key}`);
          }
        } catch (err) {
          console.error('[PWA] Error eliminando caché:', err);
        }
      }

      // 3. Limpiar historial de sincronización local para forzar un pull limpio al reiniciar
      try {
        await storage.clearSyncMetadata();
        console.log('[PWA] Historial de sincronización restablecido con éxito.');
      } catch (err) {
        console.error('[PWA] Error al restablecer historial de sincronización:', err);
      }

      // 4. Recordar la versión para no recargar en bucle y recargar con cache bust
      try { sessionStorage.setItem(UPDATE_RELOAD_KEY, serverVersion); } catch { /* noop */ }
      const cleanUrl = window.location.origin + window.location.pathname + '?v=' + Date.now();
      console.log('[PWA] Redireccionando para refrescar:', cleanUrl);
      window.location.href = cleanUrl;
      return 'ok';
    } finally {
      // Si se navegó, la página se descarta; si no, liberar el guard
      updatingRef.current = false;
    }
  };

  const handleCheckUpdates = async (manual = false) => {
    if (checkingRef.current || updatingRef.current) return;
    checkingRef.current = true;
    if (manual) {
      setUpdateStatus('checking');
    }
    
    try {
      console.log(`[PWA] Comprobando versión... Local: ${__APP_VERSION__}`);
      
      // Proactivamente llamamos al update de SW estándar
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      }

      // Verificamos la versión en caliente con el endpoint del backend
      const res = await fetch(`${API_BASE_URL}/status`, { cache: 'no-store' });
      if (!res.ok) throw new Error('Respuesta del servidor inválida');
      const data = await res.json();
      
      if (data && data.success && data.data && data.data.version) {
        const serverVersion = String(data.data.version);
        const clientVersion = __APP_VERSION__;
        
        console.log(`[PWA] Versiones -> Local: ${clientVersion} | Servidor: ${serverVersion}`);
        
        if (serverVersion !== 'dev' && clientVersion !== 'dev' && serverVersion !== clientVersion) {
          console.log('[PWA] ¡Nueva versión detectada! Iniciando actualización forzada...');
          setUpdateStatus('new-version');
          const resultado = await forceAppUpdate(serverVersion, manual);
          if (resultado !== 'ok') {
            setUpdateStatus('idle');
            if (manual) {
              const motivos: Record<Exclude<UpdateResult, 'ok'>, string> = {
                pending: 'hay registros sin sincronizar, una marca en curso o una sincronización en proceso. Sincronice los pendientes y espere a que terminen las marcas, luego intente nuevamente.',
                offline: 'no se pudo descargar la nueva versión (sin conexión o servidor no disponible). Verifique la conexión e intente nuevamente.',
                guard: 'ya hay una actualización en curso. Espere unos segundos e intente nuevamente.',
              };
              toast.info(`Hay una nueva versión (v${serverVersion}), pero la actualización se pospuso: ${motivos[resultado]}`);
            }
          }
        } else {
          try { sessionStorage.removeItem(UPDATE_RELOAD_KEY); } catch { /* noop */ }
          if (manual) {
            setUpdateStatus('up-to-date');
            toast.success(`La aplicación está al día.\n\nVersión actual: v${clientVersion}`);
            setUpdateStatus('idle');
          }
        }
      } else {
        throw new Error('Formato de versión desconocido');
      }
    } catch (err) {
      console.warn('[PWA] Error al verificar versión del sistema:', err);
      if (manual) {
        setUpdateStatus('error');
        toast.error('No se pudo verificar la versión en este momento. Intente más tarde.');
        setUpdateStatus('idle');
      }
    } finally {
      checkingRef.current = false;
    }
  };

  // Autoejecución periódica y al foco para iOS standalone PWAs
  useEffect(() => {
    // Comprobación inicial pasados 3 segundos para no bloquear la carga principal
    const initTimer = setTimeout(() => {
      handleCheckUpdates(false);
    }, 3000);

    // Comprobación cuando el usuario reanuda/enfoca la app
    const onFocus = () => handleCheckUpdates(false);
    window.addEventListener('focus', onFocus);

    // Comprobación periódica cada 60 segundos
    const periodicTimer = setInterval(() => {
      handleCheckUpdates(false);
    }, 60000);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('focus', onFocus);
      clearInterval(periodicTimer);
    };
  }, []);

  // Custom hook to stabilize viewport heights and scale on iOS / PWA standalone
  useIOSViewportStabilizer(activeTab);

  // Verificar sesión al montar
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userStr = localStorage.getItem('auth_user');
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        // Sesiones guardadas antes de que el login incluyera la compañía: un no-admin
        // sin compania_id (ni nombre válido) no puede resolver su compañía → forzar
        // re-login. (compania_id: null explícito = sin compañía asignada → aviso.)
        if (!user || (user.scope !== 'admin' && !('compania_id' in user) && !resolverCompaniaUsuario(user))) {
          throw new Error('Sesión sin compañía');
        }
        setSession({ token, user });
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }
    setCheckingSession(false);
  }, []);

  // RF-10: Prevención de Pérdida de Datos
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (pendingCount > 0 || getMarcasEnVuelo() > 0) {
        e.preventDefault();
        e.returnValue = 'Tienes registros pendientes de sincronizar. ¿Seguro que deseas salir?';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingCount]);

  // Verificar inicialización cuando hay sesión
  useEffect(() => {
    if (!session || session.user.debe_cambiar_password) return;
    checkInicializado();
  }, [session]);

  const checkInicializado = async () => {
    try {
      setLoading(true);
      const cadetes = await storage.getCadetes();
      const activos = cadetes.filter((c) => c.activo);
      if (activos.length === 0) { 
        setInicializado(false); 
        return; 
      }

      try {
        const status = await api.getInitStatus();
        if (status.data.inicializado && activos.length < status.data.totalCadetes) {
          setInicializado(false);
          return;
        }
      } catch {
        // Modo offline: usar datos locales
      }

      setInicializado(true);
    } catch (err) {
      console.error('App: Error crítico al verificar inicialización:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (token: string, user: UsuarioSesion) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    setSession({ token, user });
  };

  const handleLogout = async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      // (a) Marcas en cuenta regresiva / esperando geolocalización: confirmarlas ya
      // en IndexedDB para que cuenten como pendientes (y no perderlas en silencio).
      if (getMarcasEnVuelo() > 0) {
        solicitarFlushMarcas();
        const listas = await esperarMarcasEnVuelo(8000);
        if (!listas && !confirm('Hay marcas que todavía se están registrando. Si cierra sesión ahora podrían perderse.\n\n¿Cerrar sesión igualmente?')) {
          return;
        }
      }

      // Dar unos segundos a una sincronización en curso (p. ej. la auto-sync de la
      // marca recién confirmada) para que el conteo de pendientes sea real.
      for (let i = 0; i < 25 && isSyncingRef.current; i++) {
        await new Promise((r) => setTimeout(r, 200));
      }

      // (b) Datos sin sincronizar: se perderán (no deben heredarse a otro usuario)
      let pendientes = 0;
      try {
        pendientes = await storage.getPendingTotalCount();
      } catch (err) {
        console.warn('No se pudo verificar registros pendientes antes de cerrar sesión:', err);
        pendientes = -1;
      }
      if (pendientes !== 0) {
        const detalle = pendientes > 0
          ? `Hay ${pendientes} registro(s) sin sincronizar.`
          : 'No se pudo verificar si hay registros sin sincronizar.';
        if (!confirm(`${detalle} Si cierra sesión se borrarán de este dispositivo y NO llegarán al servidor.\n\nCancele y sincronice primero si tiene conexión.\n\n¿Cerrar sesión igualmente?`)) {
          return;
        }
      }

      // Dispositivo compartido: el próximo usuario no hereda datos (ni pendientes) ajenos
      try {
        await storage.clearAll();
      } catch (err) {
        console.error('Error al limpiar datos locales al cerrar sesión:', err);
      }

      cerrarSesionLocal();
    } finally {
      loggingOutRef.current = false;
    }
  };

  const cerrarSesionLocal = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem(COMPANIA_STORAGE_KEY);
    setCompaniaSeleccionada('Décima Compañía');
    // Respuestas /api cacheadas por el Service Worker (NetworkFirst) del usuario anterior
    void storage.clearApiCache();
    setSession(null);
  };

  // ── Render gates ────────────────────────

  if (checkingSession) return <LoadingScreen message="Verificando credenciales..." />;
  if (!session) return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  if (debeCambiarPassword) {
    return (
      <CambiarPasswordScreen
        usuario={session.user}
        onCambiada={handleLoginSuccess}
        onLogout={() => { void handleLogout(); }}
      />
    );
  }
  if (ruta === RUTA_ADMIN && isAdmin) {
    return (
      <ErrorCargaAdmin onVolver={volverDeAdmin}>
        <Suspense fallback={<LoadingScreen message="Abriendo administración..." />}>
          <AdminPanel usuarioActualId={session.user.id} onVolver={volverDeAdmin} />
        </Suspense>
      </ErrorCargaAdmin>
    );
  }
  if (companiaNoResuelta) {
    return (
      <div
        className="min-h-[100dvh] flex flex-col items-center justify-center gap-4 p-6 text-center"
        style={{ backgroundColor: 'var(--color-sgcc-surface)' }}
      >
        <p className="font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--color-sgcc-primary)' }}>
          Compañía no asignada
        </p>
        <p className="text-sm max-w-sm" style={{ color: 'var(--color-sgcc-outline)' }}>
          Su usuario no tiene una compañía válida asignada, por lo que no se puede mostrar la nómina.
          Solicite a un administrador que le asigne una compañía y vuelva a iniciar sesión.
        </p>
        <button
          className="h-11 px-6 rounded-xl text-xs font-bold uppercase tracking-wider text-white tap-active"
          style={{ backgroundColor: 'var(--color-sgcc-primary)' }}
          onClick={() => { void handleLogout(); }}
        >
          Cerrar sesión
        </button>
      </div>
    );
  }
  if (loading) return <LoadingScreen message="Preparando sistema..." />;

  if (!inicializado) {
    return <InitScreen onInicializado={() => setInicializado(true)} />;
  }

  // Subtítulo según tab
  const getTabSubtitle = () => {
    const d = new Date(fecha + 'T00:00:00');
    const fStr = d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    const ciaStr = ` · ${companiaSeleccionada}`;

    if (activeTab === 'dashboard') return `Jornada · ${fStr}${ciaStr}`;
    if (activeTab === 'presentismo') return `Jornada · ${fStr}${ciaStr}`;
    if (activeTab === 'gestion') return `Gestión de cadetes${ciaStr}`;
    if (activeTab === 'reportes') return `Generación de informes · ${fStr}${ciaStr}`;
    return '';
  };

  const getTabTitle = () => {
    if (activeTab === 'dashboard') return 'Índice';
    if (activeTab === 'presentismo') return 'Nómina de Presentismo';
    if (activeTab === 'gestion') return 'Gestión';
    if (activeTab === 'reportes') return 'Reportes';
    return '';
  };

  // ── Main layout ─────────────────────────
  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: 'var(--color-sgcc-surface)' }}
    >
      {/* Fixed TopAppBar */}
      <TopAppBar
        userName={session.user.nombres}
        userRole={session.user.jerarquia || 'Operador'}
        isAdmin={session.user.scope === 'admin'}
        companiaSeleccionada={companiaSeleccionada}
        onCompaniaChange={handleCompaniaChange}
        onLogout={() => { void handleLogout(); }}
        onCheckUpdates={() => handleCheckUpdates(true)}
        onOpenAdmin={isAdmin ? () => navegar(RUTA_ADMIN, { estado: { desdeApp: true } }) : undefined}
      />

      {/* Main content — offset top for TopAppBar (56px) + bottom for BottomNavBar (64px) */}
      <main
        className="flex-1 flex flex-col overflow-hidden"
        style={{
          paddingTop: 'calc(52px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Inner scrollable area */}
        <div className="flex-1 flex flex-col overflow-hidden max-w-2xl w-full mx-auto">

          {/* Invitación a instalar la app nativa (apagado sin VITE_*_STORE_URL) */}
          <BannerApp />

          {/* Date selector (dashboard + presentismo need fecha) */}
          {(activeTab === 'dashboard' || activeTab === 'presentismo') && (
            <DateSelector fecha={fecha} onChange={setFecha} />
          )}

          {/* Tab header bar */}
          {activeTab !== 'dashboard' && (
            <TabHeader title={getTabTitle()} subtitle={getTabSubtitle()} />
          )}



          {/* Tab content */}
          <div className="flex-1 overflow-y-auto hide-scrollbar">
            {activeTab === 'reportes' ? (
              <ReportesTab fecha={fecha} companiaForzada={companiaForzada} />
            ) : (
              <CadetesList
                key={`${fecha}-${companiaSeleccionada}`}
                fecha={fecha}
                activeTab={activeTab}
                compania={companiaSeleccionada}
                onCompaniaChange={handleCompaniaChange}
              />
            )}
          </div>
        </div>
      </main>

      {/* Fixed BottomNavBar */}
      <BottomNavBar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Overlay de Actualización del Sistema (Evita problemas de caché en iOS) */}
      {showUpdateOverlay && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#001e40] text-white p-6 animate-fade-in">
          <div className="flex flex-col items-center max-w-sm text-center">
            {/* Logo o ícono animado premium */}
            <div className="w-16 h-16 bg-[#fed65b] rounded-[20px] flex items-center justify-center text-[#001e40] mb-6 shadow-md animate-pulse">
              <RefreshCw size={32} className="animate-spin" style={{ animationDuration: '3s' }} />
            </div>
            
            <h2 className="text-[20px] font-bold mb-2 tracking-tight">
              Actualizando Aplicación
            </h2>
            
            <p className="text-[14px] text-white/80 mb-6 leading-relaxed">
              Instalando la nueva versión de SGCC. Esto limpiará el almacenamiento de caché local para asegurar que los cambios se reflejen de inmediato.
            </p>
            
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[12px] font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4ade80] animate-ping" />
              <span>Restableciendo caché de red...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
